<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"

// ================================
// TYPES & INTERFACES
// ================================
/** Clé de période : profil unique, semaine ou week-end. */
type PeriodKey = "uniform" | "week" | "weekend"

/** Variante d’affichage du toggle « consigne nuit » (grilles legacy). */
type NightToggleVariant = "uniform" | "week" | "weekend"

// ================================
// PROPS & EMITS
// ================================
/** Erreurs vee-validate pour le triplet jour (limite basse < consigne < limite haute). */
type AlarmFormTripletErrors = {
    consigne?: string
    limhaute?: string
    limbasse?: string
}

const props = defineProps<{
    periodKey: PeriodKey
    /** Carte et titre (mode semaine / week-end distingués). */
    useCard: boolean
    /** Titre au-dessus du bloc jour (ex. Semaine). */
    sectionTitle?: string
    isHourlySeries: boolean
    nightToggleVariant: NightToggleVariant
    disabled: boolean
    startNightOptions: { label: string; value: number }[]
    endNightOptions: { label: string; value: number }[]
    /** Erreurs affichées sur le triplet jour. */
    dayThresholdErrors?: AlarmFormTripletErrors
    /** Erreurs affichées sur le triplet nuit (série horaire + nuit activée). */
    nightThresholdErrors?: AlarmFormTripletErrors
    /** Unité métier (`ConfigVariable.unit`) pour les champs seuil. */
    variableUnit?: string
}>()

const consigne = defineModel<number>("consigne", { required: true })
const limhaute = defineModel<number>("limhaute", { required: true })
const limbasse = defineModel<number>("limbasse", { required: true })
const isNight = defineModel<boolean>("isNight", { required: true })
const startNight = defineModel<number>("startNight", { required: true })
const endNight = defineModel<number>("endNight", { required: true })
const nightConsigne = defineModel<number>("nightConsigne", { required: true })
const nightLimhaute = defineModel<number>("nightLimhaute", { required: true })
const nightLimbasse = defineModel<number>("nightLimbasse", { required: true })

const emit = defineEmits<{
    change: []
}>()

const { t } = useI18n({
    messages: {
        fr: {
            alarm: {
                nightPeriod: "Période de nuit",
                nightPeriodWeek: "Période de nuit (semaine)",
                nightPeriodWeekend: "Période de nuit (week-end)",
            },
        },
    },
})

// ================================
// COMPUTED
// ================================
/** ids jour — cas uniforme sans préfixe `uniform` sur les trois champs principaux. */
const getDayInputIds = computed(() => {
    if (props.periodKey === "uniform") {
        return {
            consigne: "threshold-consigne",
            limhaute: "threshold-lim-haute",
            limbasse: "threshold-lim-basse",
        }
    }
    const p = props.periodKey
    return {
        consigne: `threshold-${p}-consigne`,
        limhaute: `threshold-${p}-lim-haute`,
        limbasse: `threshold-${p}-lim-basse`,
    }
})

const getNightInputIds = computed(() => {
    let p: "uniform" | "week" | "weekend" = "uniform"
    if (props.periodKey === "week") p = "week"
    else if (props.periodKey === "weekend") p = "weekend"
    return {
        startNight: `threshold-${p}-start-night`,
        endNight: `threshold-${p}-end-night`,
        nightConsigne: `threshold-${p}-night-consigne`,
        nightLimhaute: `threshold-${p}-night-lim-haute`,
        nightLimbasse: `threshold-${p}-night-lim-basse`,
    }
})

const getNightPanelMeta = computed(() => {
    if (props.periodKey === "uniform") {
        return {
            sectionTitle: t("alarm.nightPeriod"),
            sectionTitleClass: undefined as string | undefined,
        }
    }
    if (props.periodKey === "week") {
        return {
            sectionTitle: t("alarm.nightPeriodWeek"),
            sectionTitleClass: "text-sm",
        }
    }
    return {
        sectionTitle: t("alarm.nightPeriodWeekend"),
        sectionTitleClass: "text-sm",
    }
})

const getRootClass = computed(() => (props.useCard ? "rounded-lg" : "contents"))

/** Quatre colonnes dès `sm` si le toggle nuit est affiché (sinon le 4ᵉ bloc passe sous les trois champs). */
const getDayGridClass = computed(() => {
    const base = "grid min-w-0 flex-1 grid-cols-1 gap-4 items-end"
    if (props.isHourlySeries) {
        return `${base} sm:grid-cols-4`
    }
    return `${base} sm:grid-cols-3`
})

// ================================
// INTERACTIONS
// ================================
function onFieldsChange() {
    emit("change")
}
</script>

<template>
    <div :class="getRootClass">
        <p
            v-if="props.sectionTitle"
            class="mb-3 text-base font-medium text-primary"
        >
            {{ props.sectionTitle }}
        </p>
        <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:gap-6">
            <div :class="getDayGridClass">
                <AlarmFormTripleFields
                    v-model:consigne="consigne"
                    v-model:limhaute="limhaute"
                    v-model:limbasse="limbasse"
                    v-model:is-night="isNight"
                    :variable-unit="props.variableUnit"
                    :error-consigne="props.dayThresholdErrors?.consigne"
                    :error-limhaute="props.dayThresholdErrors?.limhaute"
                    :error-limbasse="props.dayThresholdErrors?.limbasse"
                    :disabled="props.disabled"
                    :input-id-consigne="getDayInputIds.consigne"
                    :input-id-limhaute="getDayInputIds.limhaute"
                    :input-id-limbasse="getDayInputIds.limbasse"
                    :show-night-toggle="props.isHourlySeries"
                    :night-toggle-variant="props.nightToggleVariant"
                    @change="onFieldsChange"
                />
            </div>
        </div>
        <template v-if="props.isHourlySeries">
            <AlarmFormNightPanel
                v-if="isNight"
                v-model:start-night="startNight"
                v-model:end-night="endNight"
                v-model:night-consigne="nightConsigne"
                v-model:night-limhaute="nightLimhaute"
                v-model:night-limbasse="nightLimbasse"
                :variable-unit="props.variableUnit"
                :night-threshold-errors="props.nightThresholdErrors"
                :section-title="getNightPanelMeta.sectionTitle"
                :section-title-class="getNightPanelMeta.sectionTitleClass"
                :disabled="props.disabled"
                :input-id-start-night="getNightInputIds.startNight"
                :input-id-end-night="getNightInputIds.endNight"
                :input-id-night-consigne="getNightInputIds.nightConsigne"
                :input-id-night-limhaute="getNightInputIds.nightLimhaute"
                :input-id-night-limbasse="getNightInputIds.nightLimbasse"
                :start-night-options="props.startNightOptions"
                :end-night-options="props.endNightOptions"
                @change="onFieldsChange"
            />
        </template>
    </div>
</template>
